import React, { useState } from 'react'
import { Box, Grid, Button, Tooltip, Zoom } from '@mui/material'
import { DataGrid, type GridRenderCellParams, type GridColDef } from '@mui/x-data-grid'
import { RtkQueryHelper } from '../../../../api-services/shared/RtkQueryHelper'
import { useGetSolarForecastQuery } from '../../../../api-services/solar/solarApi'
import ErrorComponent from '../../../components/ui-widgets/ErrorComponent'
import LoadingComponent from '../../../components/ui-widgets/LoadingComponent'
import { type EditSolarPlantComponentProps } from './EditSolarPlantForm'
import InfoIcon from '@mui/icons-material/InfoOutlined'
import { ToggleOff, ToggleOn } from '@mui/icons-material'

const ViewForecasts: React.FC<EditSolarPlantComponentProps> = ({ solarProject }: EditSolarPlantComponentProps) => {
  const [selectedForecastId, setSelectedForecastId] = useState<number | null>(null)

  const skipQuery = selectedForecastId === null
  const solarProjectId = solarProject.identifier.solarProjectId
  const forecastId = selectedForecastId === null ? 0 : selectedForecastId
  const forecastApiResult = useGetSolarForecastQuery({ solarProjectId, forecastId }, { skip: skipQuery })

  const isForecastDataLoaded = (): boolean => {
    if (forecastApiResult.error != null) return false
    if (forecastApiResult.isLoading) return false
    if (forecastApiResult.isFetching) return false
    return true
  }

  const columns: GridColDef[] = [
    { field: 'forecastId', headerName: 'Forecast ID', flex: 10 },
    {
      field: 'forecastType',
      headerName: 'Forecast Type',
      flex: 20,
      renderCell: (params) => {
        return (
          <Tooltip title={params.row.forecastDescription} TransitionComponent={Zoom} arrow placement="bottom">
            <div>{params.row.forecastType}</div>
          </Tooltip>
        )
      }
    },
    { field: 'metricCode', headerName: 'Metric', flex: 15 },
    { field: 'unit', headerName: 'Unit', flex: 15 },
    {
      field: 'active',
      headerName: 'Active',
      flex: 15,
      renderCell: (params: GridRenderCellParams<string>) => {
        return (
          params.row.active === true
            ? (
              <ToggleOn color={'primary'}/>
              )
            : (
              <ToggleOff color={'disabled'}/>
              )
        )
      }
    },
    { field: 'validFromFormatted', headerName: 'Valid From', flex: 15 },
    {
      field: 'viewSolarPlantId', // Don't repeat ID - does weird things with the ordering of the columns
      headerName: '',
      valueGetter: (params) => params.row.forecastId,
      renderCell: (params: GridRenderCellParams<string>) => (
        <Button onClick={(e) => {
          setSelectedForecastId(params.row.forecastId)
        }} type={'button'} variant={'text'}>
          <InfoIcon/>
        </Button>
      )
    }
  ]

  const getValueForMonth = (row: number, monthIndex: number): string => {
    if (isForecastDataLoaded() && forecastApiResult.data !== null && forecastApiResult.data !== undefined) {
      const rowValues = forecastApiResult.data.forecastMap?.get(row)
      return (rowValues != null) ? rowValues[monthIndex] : ''
    } else {
      return '-'
    }
  }

  const forecastColumns: GridColDef[] = [
    { field: 'Year', flex: 10, valueGetter: (params) => params.row },
    { field: 'Jan', flex: 10, valueGetter: (params) => getValueForMonth(params.row, 0) },
    { field: 'Feb', flex: 10, valueGetter: (params) => getValueForMonth(params.row, 1) },
    { field: 'Mar', flex: 10, valueGetter: (params) => getValueForMonth(params.row, 2) },
    { field: 'Apr', flex: 10, valueGetter: (params) => getValueForMonth(params.row, 3) },
    { field: 'May', flex: 10, valueGetter: (params) => getValueForMonth(params.row, 4) },
    { field: 'Jun', flex: 10, valueGetter: (params) => getValueForMonth(params.row, 5) },
    { field: 'Jul', flex: 10, valueGetter: (params) => getValueForMonth(params.row, 6) },
    { field: 'Aug', flex: 10, valueGetter: (params) => getValueForMonth(params.row, 7) },
    { field: 'Sep', flex: 10, valueGetter: (params) => getValueForMonth(params.row, 8) },
    { field: 'Oct', flex: 10, valueGetter: (params) => getValueForMonth(params.row, 9) },
    { field: 'Nov', flex: 10, valueGetter: (params) => getValueForMonth(params.row, 10) },
    { field: 'Dec', flex: 10, valueGetter: (params) => getValueForMonth(params.row, 11) }
  ]

  return (
    <>
      <Box sx={{ pb: 4 }}>
        <h3>Forecasts</h3>

        <Grid container spacing={4}>
          <Grid item md={12}>
            <DataGrid
              rows={solarProject.forecasts}
              getRowId={(row) => row.forecastId}
              columns={columns}
              autoHeight={true}
              disableSelectionOnClick
              pagination
              pageSize={15}
              rowsPerPageOptions={[15]}
            />
          </Grid>

          <>
            <Grid item md={12}>
              {forecastApiResult.isLoading && <LoadingComponent/>}
              {(forecastApiResult.error != null) &&
                <ErrorComponent message={RtkQueryHelper.mapResponseError(forecastApiResult.error)}/>}

              {isForecastDataLoaded() && forecastApiResult.data !== null && forecastApiResult.data !== undefined && (
                <h3>Forecast - {forecastApiResult.data.forecast.forecastType} - {forecastApiResult.data.forecast.validFromFormatted}</h3>
              )}
            </Grid>

            {isForecastDataLoaded() && forecastApiResult.data !== null && forecastApiResult.data !== undefined && (
              <Grid item md={12}>
                <DataGrid
                  rows={Array.from(forecastApiResult.data.forecastMap.keys())}
                  getRowId={(row) => row}
                  columns={forecastColumns}
                  autoHeight={true}
                  disableSelectionOnClick
                  pagination
                  pageSize={15}
                  rowsPerPageOptions={[15]}
                />
              </Grid>
            )}
          </>

        </Grid>

      </Box>
    </>
  )
}

export default ViewForecasts
